
import React, { useCallback, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import Grid from '@mui/material/Grid';
import isEmpty from '../../../is-empty';

import 'jodit/es2021/jodit.min.css';

const INLINE_STYLE_RULES = {
    table: {
        'border-collapse': 'collapse',
        'table-layout': 'fixed',
        'width': '100%',
        'margin': '1em 0',
        'font-size': '10pt',
    },
    th: {
        'border': '1px solid #ccc',
        'padding': '6px 8px',
        'vertical-align': 'top',
        'background-color': '#f5f5f5',
        'font-weight': 'bold',
        'text-align': 'left',
    },
    td: {
        'border': '1px solid #ccc',
        'padding': '6px 8px',
        'vertical-align': 'top',
    },
};

function mergeStyleAttr(existing, defaults) {
    const map = {};
    if (existing) {
        existing.split(';').forEach(rule => {
            const idx = rule.indexOf(':');
            if (idx === -1) return;
            const k = rule.slice(0, idx).trim().toLowerCase();
            const v = rule.slice(idx + 1).trim();
            if (k) map[k] = v;
        });
    }
    let changed = false;
    Object.entries(defaults).forEach(([prop, val]) => {
        if (!(prop in map)) {
            map[prop] = val;
            changed = true;
        }
    });
    if (!changed) return existing || '';
    return Object.entries(map).map(([k, v]) => `${k}: ${v}`).join('; ');
}

function ensureInlineStyles(html) {
    if (!html || typeof html !== 'string' || html.indexOf('<') === -1) return html;
    if (typeof document === 'undefined') return html;
    const container = document.createElement('div');
    container.innerHTML = html;
    let mutated = false;
    Object.entries(INLINE_STYLE_RULES).forEach(([tag, defaults]) => {
        container.querySelectorAll(tag).forEach(el => {
            const before = el.getAttribute('style') || '';
            const after = mergeStyleAttr(before, defaults);
            if (after !== before) {
                el.setAttribute('style', after);
                mutated = true;
            }
        });
    });
    return mutated ? container.innerHTML : html;
}

export default function RichTextField(props) {
    const { changeRichText, disabled, errors, height, id,
            index, textAreaName, value, 'data-cy': dataCy } = props;

    const config = useMemo(() => ({
        readonly: !!disabled,
        height: height,
        width: '100%',
        toolbarAdaptive: false,
        toolbarSticky: false,
        statusbar: false,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        showPlaceholder: false,
        placeholder: '',
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: 'insert_clear_html',
        buttons: [
            'undo', 'redo', '|',
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'font', 'fontsize', 'paragraph', '|',
            'brush', 'eraser', '|',
            'align', 'ul', 'ol', 'outdent', 'indent', '|',
            'table', '|',
            'image', 'link', '|',
            'source', 'fullsize'
        ],
        uploader: {
            insertImageAsBase64URI: true
        },
        style: {
            fontFamily: 'Helvetica,Arial,sans-serif',
            fontSize: '14px'
        }
    }), [disabled, height]);

    // Commit the editor content to the parent on blur only — never on every
    // keystroke. jodit-react keeps its own internal value while the field has
    // focus, so the editor stays fully uncontrolled during typing: no parent
    // re-render per character (which previously re-rendered the whole screen and
    // re-fetched assets / spammed logs), and no `value` re-injection that resets
    // the caret. `value` is still passed so genuine external updates (record
    // load, reset) flow into the editor. Blur fires before a Save click moves
    // focus to the button, so the latest content is always captured.
    const handleBlur = useCallback((newValue) => {
        changeRichText(index, ensureInlineStyles(newValue));
    }, [changeRichText, index]);

    return (
      <Grid container spacing={2}>
        <Grid size={12}>
            <div
              data-cy={dataCy}
              style={{
                position: 'relative',
                border: !isEmpty(errors) ? '1px solid red' : 'none',
                paddingBottom: !isEmpty(errors) ? '30px' : '0',
              }}
            >
              <JoditEditor
                  key={id + '-' + index}
                  id={id}
                  name={textAreaName}
                  value={value}
                  config={config}
                  onBlur={handleBlur}
              />
              {!isEmpty(errors) && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '0',
                      width: '100%',
                      color: '#721c24',
                      padding: '5px',
                    }}
                  >
                        {errors}
                  </div>
              )}
            </div>
        </Grid>
      </Grid>
    );
}
